import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Bell,
    Check,
    CheckCheck,
    Trash2,
    X,
    FileText,
    GraduationCap,
    ListChecks,
    CheckSquare,
    Clock,
    ChevronRight,
    AlertTriangle,
    MessageSquare,
    ClipboardList
} from 'lucide-react';
import { useNotifications } from '../../context/NotificationContext';

const NotificationDropdown = () => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const navigate = useNavigate();
    const {
        notifications,
        unreadCount,
        loading,
        fetchNotifications,
        markAsRead,
        markAllAsRead,
        dismissNotification,
        dismissAllNotifications,
        refreshNotifications
    } = useNotifications();

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Refresh notifications when dropdown opens
    useEffect(() => {
        if (isOpen) {
            fetchNotifications();
        }
    }, [isOpen]);

    const getTaskIcon = (taskType, notificationType) => {
        // First check notification type for non-task notifications
        if (notificationType === 'message_received') {
            return <MessageSquare size={16} className="text-primary" />;
        }
        if (notificationType === 'survey_assigned') {
            return <ClipboardList size={16} className="text-purple-500" />;
        }

        // Task type icons
        switch (taskType) {
            case 'quiz':
                return <FileText size={16} className="text-purple-500" />;
            case 'training':
                return <GraduationCap size={16} className="text-blue-500" />;
            case 'checklist':
                return <ListChecks size={16} className="text-green-500" />;
            case 'message':
                return <MessageSquare size={16} className="text-primary" />;
            default:
                return <CheckSquare size={16} className="text-primary" />;
        }
    };

    const getTaskBg = (taskType, notificationType) => {
        // First check notification type for non-task notifications
        if (notificationType === 'message_received') {
            return 'bg-primary/10';
        }
        if (notificationType === 'survey_assigned') {
            return 'bg-purple-50';
        }

        // Task type backgrounds
        switch (taskType) {
            case 'quiz':
                return 'bg-purple-50';
            case 'training':
                return 'bg-blue-50';
            case 'checklist':
                return 'bg-green-50';
            case 'message':
                return 'bg-primary/10';
            default:
                return 'bg-primary/10';
        }
    };

    const getNotificationTypeIcon = (type) => {
        switch (type) {
            case 'task_due_soon':
                return <Clock size={16} className="text-orange-500" />;
            case 'task_overdue':
                return <AlertTriangle size={16} className="text-red-500" />;
            case 'message_received':
                return <MessageSquare size={16} className="text-primary" />;
            case 'survey_assigned':
                return <ClipboardList size={16} className="text-purple-500" />;
            default:
                return null;
        }
    };

    const formatTimeAgo = (date) => {
        const now = new Date();
        const created = new Date(date);
        const diffMs = now - created;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays === 1) return 'Yesterday';
        if (diffDays < 7) return `${diffDays}d ago`;
        return created.toLocaleDateString();
    };

    const formatDueDate = (dueDate) => {
        if (!dueDate) return null;

        const date = new Date(dueDate);
        const now = new Date();
        const diffTime = date - now;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays < 0) return { text: 'Overdue', color: 'text-red-600 bg-red-50' };
        if (diffDays === 0) return { text: 'Due Today', color: 'text-orange-600 bg-orange-50' };
        if (diffDays === 1) return { text: 'Due Tomorrow', color: 'text-orange-500 bg-orange-50' };
        if (diffDays <= 3) return { text: `Due in ${diffDays} days`, color: 'text-yellow-600 bg-yellow-50' };
        return null;
    };

    const handleNotificationClick = (notification) => {
        // Mark as read
        if (!notification.is_read) {
            markAsRead(notification.id);
        }

        // Navigate based on notification type
        switch (notification.notification_type) {
            case 'message_received':
                navigate('/employee/messages');
                break;
            case 'survey_assigned':
                navigate('/employee/surveys');
                break;
            default:
                // Task-related notifications
                navigate('/employee/tasks');
                break;
        }
        setIsOpen(false);
    };

    return (
        <div className="relative" ref={dropdownRef}>
            {/* Bell Button */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="relative p-2 hover:bg-gray-100 rounded-xl transition-colors"
            >
                <Bell size={20} className="text-gray-600" />
                {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold animate-pulse">
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </button>

            {/* Dropdown */}
            {isOpen && (
                <div className="absolute right-0 mt-2 w-96 bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden">
                    {/* Header */}
                    <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between bg-gray-50">
                        <div className="flex items-center gap-2">
                            <Bell size={18} className="text-gray-600" />
                            <h3 className="font-semibold text-gray-800">Notifications</h3>
                            {unreadCount > 0 && (
                                <span className="px-2 py-0.5 bg-red-100 text-red-600 rounded-full text-xs font-medium">
                                    {unreadCount} new
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-1">
                            {unreadCount > 0 && (
                                <button
                                    onClick={markAllAsRead}
                                    className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors"
                                    title="Mark all as read"
                                >
                                    <CheckCheck size={16} className="text-gray-500" />
                                </button>
                            )}
                            {notifications.length > 0 && (
                                <button
                                    onClick={dismissAllNotifications}
                                    className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors"
                                    title="Clear all"
                                >
                                    <Trash2 size={16} className="text-gray-500" />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Notifications List */}
                    <div className="max-h-96 overflow-y-auto">
                        {loading ? (
                            <div className="p-8 text-center">
                                <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full mx-auto"></div>
                                <p className="text-gray-500 text-sm mt-2">Loading...</p>
                            </div>
                        ) : notifications.length === 0 ? (
                            <div className="p-8 text-center">
                                <Bell size={32} className="text-gray-300 mx-auto mb-2" />
                                <p className="text-gray-500 text-sm">No notifications</p>
                                <p className="text-gray-400 text-xs mt-1">You're all caught up!</p>
                            </div>
                        ) : (
                            notifications.map((notification) => {
                                const dueInfo = formatDueDate(notification.due_date);
                                const isMessageNotification = notification.notification_type === 'message_received';
                                const isSurveyNotification = notification.notification_type === 'survey_assigned';
                                return (
                                    <div
                                        key={notification.id}
                                        className={`
                                            px-4 py-3 border-b border-gray-50 hover:bg-gray-50 cursor-pointer
                                            transition-colors group relative
                                            ${!notification.is_read ? 'bg-blue-50/50' : ''}
                                        `}
                                        onClick={() => handleNotificationClick(notification)}
                                    >
                                        <div className="flex items-start gap-3">
                                            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${getTaskBg(notification.task_type, notification.notification_type)}`}>
                                                {getNotificationTypeIcon(notification.notification_type) || getTaskIcon(notification.task_type, notification.notification_type)}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-start justify-between gap-2">
                                                    <p className={`text-sm ${!notification.is_read ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                                                        {notification.title}
                                                    </p>
                                                    {!notification.is_read && (
                                                        <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-1.5"></span>
                                                    )}
                                                </div>
                                                <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                                                    {notification.message}
                                                </p>
                                                <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                                                    <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs capitalize">
                                                        {isMessageNotification ? 'message' : isSurveyNotification ? 'survey' : notification.task_type}
                                                    </span>
                                                    {dueInfo && !isMessageNotification && !isSurveyNotification && (
                                                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${dueInfo.color}`}>
                                                            {dueInfo.text}
                                                        </span>
                                                    )}
                                                    <span className="text-xs text-gray-400 flex items-center gap-1">
                                                        <Clock size={12} />
                                                        {formatTimeAgo(notification.created_at)}
                                                    </span>
                                                </div>
                                            </div>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    dismissNotification(notification.id);
                                                }}
                                                className="p-1 hover:bg-gray-200 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                                                title="Dismiss"
                                            >
                                                <X size={14} className="text-gray-400" />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>

                    {/* Footer */}
                    <div className="px-4 py-2 border-t border-gray-100 bg-gray-50">
                        <button
                            onClick={() => {
                                navigate('/employee/notifications');
                                setIsOpen(false);
                            }}
                            className="w-full text-center text-sm text-primary hover:text-primary-hover font-medium py-1 flex items-center justify-center gap-1"
                        >
                            View all notifications
                            <ChevronRight size={16} />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationDropdown;
